import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";

// Brand Clerk here once: it styles sign-in, sign-up and the user menu. Dark app: `yarn add @clerk/ui@1.38.1`,
// `import { dark } from "@clerk/ui/themes"`, then add `theme: dark, captcha: { theme: "dark" }` (see Styling).
const appearance = { variables: { colorPrimary: "#059669", fontFamily: "Manrope, sans-serif", borderRadius: "0.375rem" } };
const BRAND = "GameRealm";
const localization = {
  signIn: {
    start: { title: `Sign in to ${BRAND}`, subtitle: `Welcome back! Please sign in to continue to ${BRAND}`,
      titleCombined: `Continue to ${BRAND}`, subtitleCombined: `Welcome back! Please sign in to continue to ${BRAND}` },
    password: { subtitle: `Enter your password to continue to ${BRAND}` },
    emailCode: { subtitle: `to continue to ${BRAND}` },
    forgotPassword: { subtitle_email: `First, enter the code sent to your email address` },
  },
  signUp: {
    start: { title: `Create your ${BRAND} account`, subtitle: `Welcome! Please fill in the details to get started.` },
    emailCode: { subtitle: `Enter the verification code sent to your email` },
    continue: { subtitle: `Please fill in the remaining details to continue.` },
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Starts Clerk from the backend's runtime config, so deploy's live keys need no rebuild.
// Must render inside <BrowserRouter> (see step 3) so Clerk navigates without reloading.
export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  // Retries while the backend restarts (e.g. right after provisioning): the app's
  // Clerk components crash if they ever render without <ClerkProvider>.
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const cfg = await (await fetch(`${BACKEND_URL}/api/clerk/config`)).json();
        if (cfg.enabled) return cancelled || setConfig(cfg);
      } catch {}
      if (cancelled) return;
      if (attempt >= 20) return setUnavailable(true);
      setTimeout(() => load(attempt + 1), 1500);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (unavailable) return <p style={{ padding: 24 }}>Sign-in is unavailable right now. Refresh the page to try again.</p>;
  if (!config) return null;
  return (
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={appearance}
      localization={localization}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/admin"
      signUpFallbackRedirectUrl="/admin"
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}
